import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Skeleton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Button, IconButton, Stack,
} from '@mui/material';
import {
  Park, Warning, Build, LocationOn, CheckCircle, Assignment,
  Schedule, ErrorOutline, TrendingUp, TrendingDown, TrendingFlat,
  EventBusy, ArrowForward, GppMaybe, ReportProblem, Close, NewReleases,
} from '@mui/icons-material';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area, Line, LineChart,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { releases } from '../data/releases';
import { APP_VERSION } from '../data/version';
import { reportsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface DashboardData {
  trees: any;
  recentControls: any[];
  openMeasures: any[];
  openDamages: any[];
}

// Schlüssel-Konstante muss mit Trees.tsx übereinstimmen
const TREES_FILTER_KEY = 'baumwaechter.trees.filters';

// Setzt Trees-Filter via sessionStorage + navigiert dorthin.
// Trees.tsx liest beim Mount aus dem gleichen Key.
function navigateToTreesWithFilter(navigate: any, filters: Record<string, any>) {
  try {
    sessionStorage.setItem(TREES_FILTER_KEY, JSON.stringify({
      // Alle Felder die Trees.tsx erwartet, sonst Default-Werte
      search: '',
      massnahmenStatus: 'all',
      schaedenStatus: 'all',
      kontrollenStatus: 'all',
      filterGefaellt: false,
      filterZustand: '',
      filterOrt: '',
      filterVerkehrssicherheit: '',
      filterMassnahmeArt: '',
      sortBy: 'updated_at',
      sortOrder: 'desc',
      ...filters,
    }));
  } catch {/* ignore quota */}
  navigate('/trees');
}

// Stat card component
const StatCard = ({ label, value, icon, color, sub, onClick }: any) => (
  <Card sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 3 } : {}, height: '100%' }} onClick={onClick}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '14px !important', height: '100%' }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: color + '15', color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{typeof value === 'number' ? value.toLocaleString('de-DE') : value}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{label}</Typography>
        <Typography variant="caption" sx={{ display: 'block', color: sub?.color || 'text.secondary', fontSize: '0.68rem', minHeight: '1em' }}>
          {sub?.text || '\u00A0'}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

// Trend card with sparkline + delta indicator
const TrendCard = ({
  label, series, delta, color, invertColors = false,
}: {
  label: string;
  series: Array<{ monat: string; anzahl: number }>;
  delta: { current: number; previous: number; delta_abs: number; delta_pct: number | null };
  color: string;
  // Falls true: weniger ist gut (z.B. neue Schäden). Pfeil unten → grün, oben → orange.
  invertColors?: boolean;
}) => {
  const positive = (delta?.delta_abs ?? 0) > 0;
  const negative = (delta?.delta_abs ?? 0) < 0;
  const isImprovement = invertColors ? negative : positive;
  const isWorse = invertColors ? positive : negative;

  const trendColor =
    delta?.delta_abs === 0 ? 'text.secondary'
    : isImprovement ? 'success.main'
    : isWorse ? 'warning.main'
    : 'text.secondary';

  const TrendIcon =
    delta?.delta_abs === 0 ? TrendingFlat
    : positive ? TrendingUp
    : TrendingDown;

  const chartData = (series || []).map(s => ({ monat: s.monat, anzahl: s.anzahl ?? 0 }));

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ py: '14px !important', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.68rem',
                minHeight: '2.2em',
                lineHeight: 1.1,
              }}
            >
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1, mt: 0.5 }}>
              {(delta?.current ?? 0).toLocaleString('de-DE')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: trendColor, flexShrink: 0, mt: 0.2 }}>
            <TrendIcon sx={{ fontSize: 18 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'inherit' }}>
              {delta?.delta_pct === null
                ? (delta?.delta_abs > 0 ? `+${delta.delta_abs}` : delta?.delta_abs ?? 0)
                : `${delta?.delta_pct! > 0 ? '+' : ''}${delta?.delta_pct}%`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minHeight: 50, mt: 0.5, mx: -1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
              <defs>
                <linearGradient id={`grad-${label.replace(/\s/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="anzahl"
                stroke={color}
                strokeWidth={2}
                fill={`url(#grad-${label.replace(/\s/g, '')})`}
                isAnimationActive={false}
              />
              <Tooltip
                contentStyle={{ fontSize: '0.75rem', padding: '4px 8px', borderRadius: 6 }}
                formatter={(v: any) => [v, label]}
                labelFormatter={(l: any) => l}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', mt: 0.5 }}>
          vs. Vormonat ({(delta?.previous ?? 0).toLocaleString('de-DE')})
        </Typography>
      </CardContent>
    </Card>
  );
};

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overdueControls, setOverdueControls] = useState<any[]>([]);
  const [overdueTotal, setOverdueTotal] = useState(0);
  const [upcomingControls, setUpcomingControls] = useState<any[]>([]);
  const [neverControlled, setNeverControlled] = useState<any[]>([]);
  const [neverTotal, setNeverTotal] = useState(0);
  const [myTasks, setMyTasks] = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [speciesData, setSpeciesData] = useState<any[]>([]);
  const [trends, setTrends] = useState<any>(null);
  // Total-Counts aus Backend (Listen sind oben auf 10 limitiert) – FEAT-020
  const [openMeasuresTotal, setOpenMeasuresTotal] = useState(0);
  const [openDamagesTotal, setOpenDamagesTotal] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  // „Was ist neu"-Banner: zeigt das aktuelle Release, wegklickbar. Die weggeklickte
  // Version wird lokal gemerkt → erscheint erst beim nächsten Release wieder.
  const latestRelease = releases.find((r: any) => r.version === APP_VERSION) || releases[0];
  const [showNews, setShowNews] = useState(() => {
    try { return localStorage.getItem('bw.dismissedRelease') !== APP_VERSION; } catch { return true; }
  });
  const dismissNews = () => {
    try { localStorage.setItem('bw.dismissedRelease', APP_VERSION); } catch {}
    setShowNews(false);
  };

  // Helper: unterstützt sowohl altes Array- als auch neues {items,total}-Format
  // damit das Frontend auch funktioniert bevor das Backend neu deployed ist.
  const unwrapList = (resData: any): { items: any[]; total: number } => {
    if (Array.isArray(resData)) return { items: resData, total: resData.length };
    if (resData && Array.isArray(resData.items)) return { items: resData.items, total: resData.total ?? resData.items.length };
    return { items: [], total: 0 };
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [dashRes, overdueRes, upcomingRes, neverRes, timelineRes, speciesRes, trendsRes, myTasksRes] = await Promise.all([
          reportsAPI.getDashboard(),
          api.get('/reports/overdue-controls').catch(() => ({ data: [] })),
          api.get('/reports/upcoming-controls').catch(() => ({ data: [] })),
          api.get('/reports/never-controlled').catch(() => ({ data: [] })),
          api.get('/reports/controls-timeline').catch(() => ({ data: [] })),
          api.get('/reports/trees-by-species').catch(() => ({ data: [] })),
          api.get('/reports/trends').catch(() => ({ data: null })),
          api.get('/measures/my-tasks').catch(() => ({ data: [] })),
        ]);
        setData(dashRes.data);
        // Total counts aus dem Dashboard-Endpoint (FEAT-020)
        setOpenMeasuresTotal(dashRes.data?.openMeasuresTotal ?? dashRes.data?.openMeasures?.length ?? 0);
        setOpenDamagesTotal(dashRes.data?.openDamagesTotal ?? dashRes.data?.openDamages?.length ?? 0);

        const overdue = unwrapList(overdueRes.data);
        setOverdueControls(overdue.items);
        setOverdueTotal(overdue.total);

        const upcoming = unwrapList(upcomingRes.data);
        setUpcomingControls(upcoming.items);

        const never = unwrapList(neverRes.data);
        setNeverControlled(never.items);
        setNeverTotal(never.total);

        setTimeline(timelineRes.data);
        setSpeciesData(speciesRes.data.slice(0, 10));
        setTrends(trendsRes.data);
        setMyTasks(myTasksRes.data);
      } catch {
        setError('Dashboard-Daten konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return (
    <Grid container spacing={2}>
      {[1,2,3,4,5,6].map(i => <Grid item xs={6} md={2} key={i}><Skeleton variant="rounded" height={90} /></Grid>)}
      <Grid item xs={12} md={6}><Skeleton variant="rounded" height={300} /></Grid>
      <Grid item xs={12} md={6}><Skeleton variant="rounded" height={300} /></Grid>
    </Grid>
  );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const { trees, recentControls = [], openMeasures = [], openDamages = [] } = data;
  const totalTrees = parseInt(trees.total_trees) || 0;
  const withDamage = parseInt(trees.trees_with_damage) || 0;
  const locations = parseInt(trees.standorte) || 0;
  const baumarten = parseInt(trees.baumarten) || 0;
  const nichtVerkehrssicher = parseInt(trees.nicht_verkehrssicher) || 0;
  const gefahrImVerzug = parseInt(trees.gefahr_im_verzug) || 0;

  const pieData = [
    { name: 'Gesund',         value: parseInt(trees.gesund) || 0,                color: '#2e7d32' }, // kräftiges Grün
    { name: 'Leicht gesch.',  value: parseInt(trees.leicht_geschaedigt) || 0,    color: '#f9a825' }, // satter Gelb
    { name: 'Stärker gesch.', value: parseInt(trees.staerker_geschaedigt) || 0,  color: '#e65100' }, // dunkles Orange
    { name: 'Stark gesch.',   value: parseInt(trees.stark_geschaedigt) || 0,     color: '#c62828' }, // tiefes Rot
    { name: 'Abgestorben',    value: parseInt(trees.abgestorben) || 0,           color: '#212121' }, // fast Schwarz
    { name: 'Gefällt',        value: parseInt(trees.gefaellt) || 0,              color: '#6d4c41' }, // Holzbraun
  ].filter(d => d.value > 0);

  // Format timeline months
  const monthNames: Record<string, string> = { '01':'Jan','02':'Feb','03':'Mär','04':'Apr','05':'Mai','06':'Jun','07':'Jul','08':'Aug','09':'Sep','10':'Okt','11':'Nov','12':'Dez' };
  const timelineFormatted = timeline.map((t: any) => ({
    monat: monthNames[t.monat?.split('-')[1]] || t.monat,
    Kontrollen: parseInt(t.anzahl) || 0,
  }));

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>Willkommen, {user?.name}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Übersicht · {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      </Typography>

      {/* „Was ist neu"-Banner (wegklickbar, kommt erst beim nächsten Release wieder) */}
      {showNews && latestRelease && (
        <Card sx={{ mb: 2.5, border: '1px solid', borderColor: '#4a7c59', bgcolor: 'rgba(74,124,89,0.06)' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <NewReleases sx={{ color: '#4a7c59', mt: 0.2 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={800}>
                  Neu in v{latestRelease.version}{latestRelease.title ? `: ${latestRelease.title}` : ''}
                </Typography>
                <Stack component="ul" spacing={0.3} sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {(latestRelease.entries || []).slice(0, 4).map((e: any, i: number) => (
                    <Typography key={i} component="li" variant="caption" color="text.secondary">{e.text}</Typography>
                  ))}
                </Stack>
                <Button size="small" onClick={() => navigate('/release-notes')} sx={{ mt: 0.5, textTransform: 'none' }}>
                  Alle Neuerungen ansehen
                </Button>
              </Box>
              <IconButton size="small" onClick={dismissNews} aria-label="Ausblenden"><Close fontSize="small" /></IconButton>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Handlungsbedarf – akute Punkte für die Verkehrssicherungspflicht */}
      {(nichtVerkehrssicher > 0 || gefahrImVerzug > 0 || overdueTotal > 0 || openMeasuresTotal > 0) && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={800}
            sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.72rem' }}>
            Handlungsbedarf
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><StatCard label="Nicht verkehrssicher" value={nichtVerkehrssicher} icon={<GppMaybe />} color={nichtVerkehrssicher > 0 ? '#c62828' : '#4caf50'}
              sub={nichtVerkehrssicher > 0 ? { text: 'Sofort prüfen!', color: '#c62828' } : { text: 'Keine' }}
              onClick={() => navigateToTreesWithFilter(navigate, { filterVerkehrssicherheit: 'Nicht gegeben' })} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Gefahr im Verzug" value={gefahrImVerzug} icon={<ReportProblem />} color={gefahrImVerzug > 0 ? '#c62828' : '#4caf50'}
              sub={gefahrImVerzug > 0 ? { text: 'Dringend', color: '#c62828' } : { text: 'Keine' }}
              onClick={() => navigateToTreesWithFilter(navigate, { filterZustand: 'Gefahr im Verzug' })} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Kontrolle überfällig" value={overdueTotal} icon={<ErrorOutline />} color={overdueTotal > 0 ? '#e65100' : '#4caf50'}
              sub={overdueTotal > 0 ? { text: 'Sofort prüfen!', color: '#e65100' } : { text: 'Alles aktuell' }}
              onClick={() => navigateToTreesWithFilter(navigate, { kontrollenStatus: 'overdue' })} /></Grid>
            <Grid item xs={6} md={3}><StatCard label="Offene Maßnahmen" value={openMeasuresTotal} icon={<Build />} color="#1565c0"
              onClick={() => navigateToTreesWithFilter(navigate, { massnahmenStatus: 'open' })} /></Grid>
          </Grid>
        </Box>
      )}

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={6} md={3}><StatCard label="Bäume gesamt" value={totalTrees} icon={<Park />} color="#4a7c59"
          onClick={() => navigateToTreesWithFilter(navigate, {})}
          sub={{ text: `${baumarten} Baumarten · ${locations} Standorte` }} /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Offene Schäden" value={openDamagesTotal} icon={<Warning />} color="#e65100"
          sub={withDamage > 0 ? { text: `${withDamage} Bäume betroffen`, color: '#e65100' } : undefined}
          onClick={() => navigateToTreesWithFilter(navigate, { schaedenStatus: 'open' })} /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Kontrollen durchgeführt" value={recentControls.length > 0 ? recentControls.length + '+' : '0'} icon={<Assignment />} color="#4a7c59"
          onClick={() => navigateToTreesWithFilter(navigate, { kontrollenStatus: 'vorhanden' })} /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Bald fällige Kontrollen" value={upcomingControls.length} icon={<Schedule />} color="#1565c0"
          sub={upcomingControls.length > 0 ? { text: 'In den nächsten 90 Tagen' } : { text: 'Keine anstehend' }}
          onClick={() => navigateToTreesWithFilter(navigate, { kontrollenStatus: 'soon' })} /></Grid>
      </Grid>

      {/* Überfällige Kontrollen Warning */}
      {overdueControls.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: 'warning.lighter' } }}
          onClick={() => navigateToTreesWithFilter(navigate, { kontrollenStatus: 'overdue' })}
          action={<Button size="small" color="inherit" onClick={(e) => { e.stopPropagation(); navigateToTreesWithFilter(navigate, { kontrollenStatus: 'overdue' }); }}>Alle anzeigen</Button>}>
          <strong>{overdueControls.length} Bäume</strong> haben überfällige Kontrollen! Der älteste ist {overdueControls[0]?.tage_ueberfaellig} Tage überfällig.
        </Alert>
      )}

      {/* Meine Aufgaben – volle Breite direkt unter den Stat-Cards (FEAT-016) */}
      {myTasks.length > 0 && (
        <Card sx={{ mb: 2.5, border: '1px solid', borderColor: 'primary.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Assignment sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" fontWeight={700} color="primary">Meine Aufgaben</Typography>
              <Chip label={myTasks.length} size="small" color="primary" sx={{ ml: 1 }} />
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => navigate('/meine-aufgaben')} sx={{ fontSize: '0.78rem' }}>
                Alle anzeigen →
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Maßnahme</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Baum</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Ort / Straße</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Geplant</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Zugewiesen von</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myTasks.slice(0, 10).map((m: any) => (
                    <TableRow
                      key={m.id}
                      hover
                      sx={{ cursor: m.tree_id ? 'pointer' : 'default' }}
                      onClick={() => m.tree_id ? navigate(`/trees/${m.tree_id}`) : navigate('/meine-aufgaben')}
                    >
                      <TableCell>
                        <Chip
                          label={m.status === 'in Arbeit' ? '⟳ In Arbeit' : 'Offen'}
                          size="small"
                          color={m.status === 'in Arbeit' ? 'warning' : 'info'}
                          sx={{ fontWeight: 700, fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{m.measure_type}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {m.tree_id && m.baumnummer ? (
                          <>
                            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                              #{m.baumnummer}
                            </Typography>
                            {m.baumart && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                                {m.baumart}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontStyle: 'italic', color: 'text.secondary' }}>
                            (frei)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {m.ort || '–'}{m.strasse ? `, ${m.strasse}` : ''}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {m.planned_date ? new Date(m.planned_date).toLocaleDateString('de-DE') : '–'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {m.assigned_by_name || '–'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Trend-Section: Sparklines mit Vormonat-Vergleich */}
      {trends && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp sx={{ fontSize: 18, color: 'primary.main' }} />
            Trends · Letzte 12 Monate
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TrendCard
                label="Bäume erfasst"
                series={trends.trees_per_month}
                delta={trends.deltas.trees_per_month}
                color="#4a7c59"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TrendCard
                label="Schäden eröffnet"
                series={trends.damages_opened_per_month}
                delta={trends.deltas.damages_opened_per_month}
                color="#e65100"
                invertColors
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TrendCard
                label="Schäden behoben"
                series={trends.damages_resolved_per_month}
                delta={trends.deltas.damages_resolved_per_month}
                color="#2e7d32"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TrendCard
                label="Maßnahmen geplant"
                series={trends.measures_created_per_month}
                delta={trends.deltas.measures_created_per_month}
                color="#1565c0"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TrendCard
                label="Maßnahmen erledigt"
                series={trends.measures_completed_per_month}
                delta={trends.deltas.measures_completed_per_month}
                color="#2e7d32"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <TrendCard
                label="Kontrollen"
                series={trends.controls_per_month}
                delta={trends.deltas.controls_per_month}
                color="#4a7c59"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      <Grid container spacing={2}>
        {/* Zustandsverteilung */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Zustandsverteilung</Typography>
              {pieData.length > 0 ? (<>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart><Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                    {pieData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                  </Pie><Tooltip formatter={(v: number) => v.toLocaleString('de-DE')} /></PieChart>
                </ResponsiveContainer>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                  {pieData.map(d => <Chip key={d.name} label={`${d.name}: ${d.value}`} size="small" sx={{ bgcolor: d.color+'20', color: d.color, fontWeight: 600, fontSize: '0.7rem' }} />)}
                </Box>
              </>) : <Typography color="text.secondary">Keine Daten</Typography>}
            </CardContent>
          </Card>
        </Grid>

        {/* Kontrollen Timeline */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Kontrollen (letzte 12 Monate)</Typography>
              {timelineFormatted.length > 0 ? (
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={timelineFormatted}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="monat" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="Kontrollen" fill="#4a7c59" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <Assignment sx={{ fontSize: 40, mb: 1, color: '#ccc' }} />
                  <Typography variant="body2">Noch keine Kontrollen durchgeführt</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Baumarten */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Top Baumarten</Typography>
              {speciesData.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {speciesData.map((s: any, i: number) => (
                    <Box key={s.baumart} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ width: 18, color: 'text.secondary', fontSize: '0.7rem' }}>{i+1}.</Typography>
                      <Box sx={{ flex: 1, position: 'relative', height: 24, bgcolor: '#f5f5f5', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${(parseInt(s.count) / parseInt(speciesData[0].count)) * 100}%`, bgcolor: '#4a7c59' + (i === 0 ? '' : '80'), borderRadius: 1 }} />
                        <Typography variant="caption" sx={{ position: 'relative', zIndex: 1, px: 1, lineHeight: '24px', fontWeight: i === 0 ? 700 : 400, fontSize: '0.72rem', color: (parseInt(s.count) / parseInt(speciesData[0].count)) > 0.4 ? 'white' : '#333' }}>
                          {s.baumart} ({parseInt(s.count).toLocaleString('de-DE')})
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : <Typography color="text.secondary">Keine Daten</Typography>}
            </CardContent>
          </Card>
        </Grid>

        {/* Überfällige Kontrollen */}
        {overdueControls.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card sx={{ border: '1px solid #ffcc80' }}>
              <CardContent>
                <Box
                  onClick={() => navigateToTreesWithFilter(navigate, { kontrollenStatus: 'overdue' })}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
                    cursor: 'pointer', borderRadius: 1, mx: -0.5, px: 0.5, py: 0.3,
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: 'action.hover', '& .header-arrow': { opacity: 1, transform: 'translateX(2px)' } },
                  }}
                  title="In Baumkataster mit Filter Überfällige Kontrollen öffnen"
                >
                  <ErrorOutline sx={{ color: '#c62828' }} />
                  <Typography variant="subtitle1" fontWeight={700} color="error">Überfällige Kontrollen</Typography>
                  <Chip label={overdueTotal} size="small" color="error" sx={{ ml: 'auto' }} />
                  <ArrowForward className="header-arrow" sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.4, transition: 'opacity 0.15s, transform 0.15s' }} />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Baum</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Ort</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Letzte Kontrolle</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Intervall</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Überfällig</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {overdueControls.slice(0, 8).map((c: any) => (
                        <TableRow key={c.tree_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/trees/${c.tree_id}`)}>
                          <TableCell><Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>#{c.baumnummer}</Typography></TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{c.ort || '–'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{c.letzte_kontrolle ? new Date(c.letzte_kontrolle).toLocaleDateString('de-DE') : '–'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{c.kontroll_intervall || '–'}</TableCell>
                          <TableCell>
                            <Chip label={`${c.tage_ueberfaellig} Tage`} size="small"
                              color={parseInt(c.tage_ueberfaellig) > 180 ? 'error' : 'warning'}
                              sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Bald fällige Kontrollen */}
        {upcomingControls.length > 0 && (
          <Grid item xs={12} md={overdueControls.length > 0 ? 6 : 12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Schedule sx={{ color: '#1565c0' }} />
                  <Typography variant="subtitle1" fontWeight={700}>Bald fällige Kontrollen (90 Tage)</Typography>
                  <Chip label={upcomingControls.length} size="small" color="info" sx={{ ml: 'auto' }} />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Baum</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Ort</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Fällig am</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {upcomingControls.slice(0, 8).map((c: any) => (
                        <TableRow key={c.tree_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/trees/${c.tree_id}`)}>
                          <TableCell><Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>#{c.baumnummer}</Typography></TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{c.ort || '–'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{c.naechste_kontrolle ? new Date(c.naechste_kontrolle).toLocaleDateString('de-DE') : '–'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Nie kontrollierte Bäume (v1.5.0) */}
        {neverControlled.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  onClick={() => navigateToTreesWithFilter(navigate, { kontrollenStatus: 'never' })}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
                    cursor: 'pointer', borderRadius: 1, mx: -0.5, px: 0.5, py: 0.3,
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: 'action.hover', '& .header-arrow': { opacity: 1, transform: 'translateX(2px)' } },
                  }}
                  title="In Baumkataster mit Filter Nie kontrolliert öffnen"
                >
                  <EventBusy sx={{ color: '#e65100' }} />
                  <Typography variant="subtitle1" fontWeight={700}>Nie kontrolliert</Typography>
                  <Chip label={neverTotal} size="small" color="warning" sx={{ ml: 'auto' }} />
                  <ArrowForward className="header-arrow" sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.4, transition: 'opacity 0.15s, transform 0.15s' }} />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Baum</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Ort</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Erfasst seit</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {neverControlled.slice(0, 8).map((c: any) => (
                        <TableRow key={c.tree_id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/trees/${c.tree_id}`)}>
                          <TableCell><Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>#{c.baumnummer}</Typography></TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{c.ort || '–'}</TableCell>
                          <TableCell>
                            <Chip label={`${c.tage_seit_erfassung} Tage`} size="small"
                              color={parseInt(c.tage_seit_erfassung) > 365 ? 'error' : 'warning'}
                              sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Offene Schäden */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                onClick={() => navigateToTreesWithFilter(navigate, { schaedenStatus: 'open' })}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
                  cursor: 'pointer', borderRadius: 1, mx: -0.5, px: 0.5, py: 0.3,
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: 'action.hover', '& .header-arrow': { opacity: 1, transform: 'translateX(2px)' } },
                }}
                title="In Baumkataster mit Filter Offene Schäden öffnen"
              >
                <Warning sx={{ color: '#e65100' }} />
                <Typography variant="subtitle1" fontWeight={700}>Offene Schäden</Typography>
                <Chip label={openDamagesTotal} size="small" color="warning" sx={{ ml: 'auto' }} />
                <ArrowForward className="header-arrow" sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.4, transition: 'opacity 0.15s, transform 0.15s' }} />
              </Box>
              {openDamages.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}><CheckCircle sx={{ fontSize: 36, color: 'success.main', mb: 0.5 }} /><Typography variant="body2" color="text.secondary">Keine offenen Schäden</Typography></Box>
              ) : (
                <TableContainer><Table size="small"><TableHead><TableRow>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Baum</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Typ</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Priorität</TableCell>
                </TableRow></TableHead><TableBody>
                  {openDamages.slice(0, 6).map((d: any) => (
                    <TableRow key={d.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/trees/${d.tree_id}`)}>
                      <TableCell><Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>#{d.baumnummer}</Typography></TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{d.damage_type || '–'}</TableCell>
                      <TableCell><Chip label={d.priority || 'mittel'} size="small" color={d.priority === 'dringend' ? 'error' : d.priority === 'hoch' ? 'warning' : 'default'} sx={{ fontSize: '0.7rem' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table></TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Offene Maßnahmen */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box
                onClick={() => navigateToTreesWithFilter(navigate, { massnahmenStatus: 'open' })}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
                  cursor: 'pointer', borderRadius: 1, mx: -0.5, px: 0.5, py: 0.3,
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: 'action.hover', '& .header-arrow': { opacity: 1, transform: 'translateX(2px)' } },
                }}
                title="In Baumkataster mit Filter Offene Maßnahmen öffnen"
              >
                <Build sx={{ color: '#1565c0' }} />
                <Typography variant="subtitle1" fontWeight={700}>Offene Maßnahmen</Typography>
                <Chip label={openMeasuresTotal} size="small" color="primary" sx={{ ml: 'auto' }} />
                <ArrowForward className="header-arrow" sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.4, transition: 'opacity 0.15s, transform 0.15s' }} />
              </Box>
              {openMeasures.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}><CheckCircle sx={{ fontSize: 36, color: 'success.main', mb: 0.5 }} /><Typography variant="body2" color="text.secondary">Keine offenen Maßnahmen</Typography></Box>
              ) : (
                <TableContainer><Table size="small"><TableHead><TableRow>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Baum</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Maßnahme</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Geplant</TableCell>
                </TableRow></TableHead><TableBody>
                  {openMeasures.slice(0, 6).map((m: any) => (
                    <TableRow key={m.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/trees/${m.tree_id}`)}>
                      <TableCell><Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>#{m.baumnummer}</Typography></TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{m.measure_type || '–'}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{m.planned_date ? new Date(m.planned_date).toLocaleDateString('de-DE') : '–'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table></TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
